import { Box, Button as MuiButton } from "@mui/material";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import UserType from "../../constants/UserType";
import useInvoice from "../../customHooks/useInvoice";
import {
  getTotalDueForUser,
  getTotalPaidForUser,
  getTotalPendingForUser,
} from "../../firebase/finance";
import { theme } from "../../mui-customs/Theme";
import { setTotalData } from "../../redux/financeSlice";
import { RootState } from "../../redux/store";
import { formatAmountForDisplay } from "../../utils/stripe-helpers";
import Typography from "../Typography";

const FinanceSummaryCard = () => {
  const { totalData } = useSelector((state: RootState) => state.financialSlice);
  const { user } = useSelector((state: RootState) => state.user);
  const { settings } = useInvoice();
  const router = useRouter();
  const dispatch = useDispatch();
  const calculateTotalFinance = async () => {
    if (user.uid && user.userType) {
      const totalDue = await getTotalDueForUser(
        user.uid,
        user.userType,
        settings.service_charge
      );
      const totalPending = await getTotalPendingForUser(
        user.uid,
        user.userType,
        settings.service_charge,
        settings.deposit_percentage
      );
      const totalPaid = await getTotalPaidForUser(
        user.uid,
        user.userType,
        settings.service_charge
      );
      dispatch(
        setTotalData({
          totalDue,
          totalPaid,
          totalPending,
        })
      );
    }
  };

  useEffect(() => {
    calculateTotalFinance();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: theme.brand1[200],
        width: "100%",
        p: 3,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h2"
        sx={{ color: theme.black[800], textAlign: "center" }}
      >
        Financial Summary
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          mt: 2,
          position: "relative",
          "& div": {
            zIndex: 20,
            textAlign: "center",
          },
          "& div:nth-child(n+4)": {
            position: "absolute",
            height: "32px",
            borderRadius: 5,
            bottom: 0,
            left: 0,
          },
          "& div p:nth-child(2)": {
            mt: 2,
            mb: "2px",
            color: `${theme.whiteBg.WBG1} !important`,
          },
        }}
      >
        <Box>
          <Typography variant="h3">Total Due</Typography>
          <Typography variant="h3">
            {formatAmountForDisplay(totalData.totalDue, "usd")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h3">Total Pending</Typography>
          <Typography variant="h3">
            {formatAmountForDisplay(
              totalData.totalDue - totalData.totalPaid,
              "usd"
            )}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h3">Total Paid</Typography>
          <Typography variant="h3">
            {formatAmountForDisplay(totalData.totalPaid, "usd")}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "33%",
            backgroundColor: theme.brand2[500],
            zIndex: "19 !important",
          }}
        />
        <Box
          sx={{
            width: "66%",
            backgroundColor: theme.yellow[300],
            zIndex: "18 !important",
          }}
        />
        <Box
          sx={{
            width: "100%",
            backgroundColor: theme.brand1[600],
            zIndex: "17 !important",
          }}
        />
      </Box>
    </Box>
  );
};

export default FinanceSummaryCard;
